import { useContext } from "react";
import { Accept, DropEvent, useDropzone } from "react-dropzone";

import { removeNullUndefined } from "@nens/shared-utils";

import { FileUploadingContext } from "@/features/project-file-uploading";
import { useFileDirectoryQuery, useFileDirectoryQueryDirectly } from "@/queries/query-file";
import { ProjectContext } from "@/stores/project-provider";

import { useProjectActionAllowed } from "./use-action-allowed-in-project";

interface UseProjectFilesDropzoneProps {
  path: string;
  accept?: Accept;
  fileDirectoryQuery: ReturnType<typeof useFileDirectoryQuery>;
  fileDirectoryQueryDirectly: ReturnType<typeof useFileDirectoryQueryDirectly>;
}

const readDirectoryRecursively = async (
  dirEntry: FileSystemDirectoryEntry,
  parentPath: string
): Promise<File[]> => {
  const files: File[] = [];
  const reader = dirEntry.createReader();
  const currentPath = parentPath + dirEntry.name + "/";

  const readEntries = (): Promise<FileSystemEntry[]> => {
    return new Promise((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
  };

  let entries = await readEntries();

  while (entries.length > 0) {
    for (const entry of entries) {
      if (entry.isFile) {
        const file = await new Promise<File>((resolve, reject) => {
          (entry as FileSystemFileEntry).file(resolve, reject);
        });

        // Create a new File object with webkitRelativePath set
        const fileWithPath = new File([file], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        });
        Object.defineProperty(fileWithPath, "webkitRelativePath", {
          value: currentPath + file.name,
          writable: false,
        });

        files.push(fileWithPath);
      } else if (entry.isDirectory) {
        const subFiles = await readDirectoryRecursively(
          entry as FileSystemDirectoryEntry,
          currentPath
        );
        files.push(...subFiles);
      }
    }

    entries = await readEntries();
  }

  return files;
};

/**
 * Intercepts file events to preserve folder structure when dragging folders.
 * This is necessary because react-dropzone with useFsAccessApi: false doesn't preserve
 * the webkitRelativePath property or provide access to FileSystemEntry objects after processing.
 *
 * Handles three types of events:
 * 1. FileSystemFileHandle[] - when useFsAccessApi is true (not our case)
 * 2. File input events - when user selects files via file picker dialog
 * 3. Drag & drop events - when user drags files/folders onto the drop zone
 */
const getFilesFromEvent = async (event: DropEvent): Promise<File[]> => {
  // Return empty array since we don't use this API
  if (Array.isArray(event)) {
    return [];
  }

  // Handle file input events (file picker dialog)
  if ("target" in event && event.target && "files" in event.target) {
    const input = event.target as HTMLInputElement;
    return input.files ? Array.from(input.files) : [];
  }

  // Handle drag & drop events
  if ("dataTransfer" in event && event.dataTransfer) {
    const items = Array.from(event.dataTransfer.items);

    // Check if any items are directories - if so, we need async processing
    const hasDirectories = items.some((item) => {
      const entry = item.webkitGetAsEntry?.();
      return entry?.isDirectory;
    });

    // If no directories, use the simple synchronous approach with dataTransfer.files
    if (!hasDirectories) {
      const files = Array.from(event.dataTransfer.files);
      return files;
    }

    // For directories, we need async processing
    const files: File[] = [];

    for (const item of items) {
      // Skip non-file items
      if (item.kind !== "file") continue;

      const entry = item.webkitGetAsEntry?.();

      if (entry?.isDirectory) {
        // Recursively read all files in the directory and set webkitRelativePath
        const dirFiles = await readDirectoryRecursively(entry as FileSystemDirectoryEntry, "");
        files.push(...dirFiles);
      } else if (entry?.isFile) {
        const file = await new Promise<File>((resolve, reject) => {
          (entry as FileSystemFileEntry).file(resolve, reject);
        });
        files.push(file);
      }
    }

    return files;
  }

  // Unhandled event types
  return [];
};

/**
 * Handles file and folder drops/uploads.
 *
 * Implementation choices:
 * 1. Folder structure detection: Uses `getFilesFromEvent` to intercept drag events
 *    before react-dropzone processes them, allowing us to detect directories via
 *    `webkitGetAsEntry()` and preserve folder structure.
 *
 * 2. Batch uploads: When uploading folders, files are grouped by directory path
 *    and uploaded in separate batches (one per directory). The backend automatically
 *    creates parent directories as needed.
 *
 * 3. Completion callbacks: Each batch upload receives a completion callback.
 *    The UI only refetches when ALL batches complete, preventing redundant refetches
 *    and ensuring the folder structure is visible immediately after upload.
 *
 * 4. We don't manually create folders with `useAddFolder` because:
 *    - The upload API automatically creates parent directories (mentioned in 1)
 *    - `useAddFolder` is bound to the current path context and can't create nested structures
 *    - This approach requires fewer API calls and is more reliable
 *
 * @see FileUploadingContext for the batch completion tracking implementation
 */
export function useProjectFilesDropzone({
  path,
  accept,
  fileDirectoryQuery,
  fileDirectoryQueryDirectly,
}: UseProjectFilesDropzoneProps) {
  const { id: projectId } = useContext(ProjectContext);
  const { canWriteProject } = useProjectActionAllowed();
  const { uploadFilesWithOverwriteConfirm } = useContext(FileUploadingContext);

  const onDrop = async (uploadFiles: File[]) => {
    // Check if files have webkitRelativePath (indicates folder selection)
    const hasRelativePaths = uploadFiles.some((file) => file.webkitRelativePath);

    if (hasRelativePaths) {
      // Group files by their directory structure
      const filesByPath = new Map<string, File[]>();

      for (const file of uploadFiles) {
        const pathParts = file.webkitRelativePath.split("/");
        const dirPath = pathParts.slice(0, -1).join("/");

        if (!filesByPath.has(dirPath)) {
          filesByPath.set(dirPath, []);
        }
        const filesInDir = filesByPath.get(dirPath);
        if (filesInDir) {
          filesInDir.push(file);
        }
      }

      // Upload files to their respective directories
      // The upload API creates all necessary parent directories automatically
      const sortedPaths = Array.from(filesByPath.keys()).sort();

      // Start all uploads with completion callback
      const uploadsArray = removeNullUndefined(
        await Promise.all(
          sortedPaths.map(async (relativePath) => {
            const filesInDir = filesByPath.get(relativePath);
            // ignore empty folders
            if (!filesInDir) return null;

            const fullPath = path + (relativePath ? relativePath + "/" : "");

            return {
              directory: fullPath,
              files: filesInDir.map((file) => ({ file })),
              currentFiles: (await fileDirectoryQueryDirectly(fullPath)).data?.items ?? [],
            };
          })
        )
      );

      if (uploadsArray.length > 0) {
        uploadFilesWithOverwriteConfirm({
          projectId,
          uploads: uploadsArray,
        });
      }

      return;
    }

    // Regular file upload
    uploadFilesWithOverwriteConfirm({
      projectId,
      uploads: [
        {
          directory: path,
          files: uploadFiles.map((file) => ({ file })),
          currentFiles: fileDirectoryQuery.data?.items ?? [],
        },
      ],
    });
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openDropzone,
  } = useDropzone({
    onDrop,
    accept,
    noClick: true,
    disabled: !canWriteProject,

    // Do not use the new fs access API, because it is not supported by Playwright
    // see https://github.com/microsoft/playwright/issues/8850
    useFsAccessApi: false,
    getFilesFromEvent,
  });

  const openFolderPicker = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.webkitdirectory = true;
    input.multiple = true;
    input.onchange = async (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files) {
        await onDrop(Array.from(files));
      }
    };
    input.click();
    input.remove();
  };

  return {
    getRootProps,
    getInputProps,
    isDragActive,
    openDropzone,
    openFolderPicker,
  };
}
